import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildCanonicalPublicPath } from '@crm/shared/public-marketplace';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { BrandLogo } from '../brand/BrandLogo';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { HomeButton, homeButtonClasses } from '../home/HomeButton';
import {
  MarketingButton,
  editorialButtonClasses,
} from '../marketing/Editorial';
import {
  MARKETPLACE_SITE_URL,
  segmentMarketingRoutes,
} from '../../config/marketingRoutes';
import { trackMarketingEvent } from '../../lib/analytics';
import { scrollToDemoForm } from '../../lib/navigation';
import { cn } from '../../lib/utils';

type HeaderRouteNavItem = {
  readonly to: string;
  readonly labelKey: string;
  readonly external?: boolean;
};

const MOBILE_MENU_ID = 'site-mobile-menu';
const PUBLIC_MARKETPLACE_HOME_URL = `${MARKETPLACE_SITE_URL}${buildCanonicalPublicPath('peru')}`;

const ROUTE_NAV_ITEMS: readonly HeaderRouteNavItem[] = [
  {
    to: PUBLIC_MARKETPLACE_HOME_URL,
    labelKey: 'nav.marketplaceSearch',
    external: true,
  },
  ...segmentMarketingRoutes
    .filter((route) => route.showInHeader)
    .map((route) => ({
      to: route.path,
      labelKey: route.navLabelKey,
    })),
  { to: '/precios/', labelKey: 'nav.pricing' },
] as const;

export function Header(): React.JSX.Element {
  const { t } = useTranslation(['common', 'segments']);
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isHomePage = location.pathname === '/';

  // The open menu covers the page: stop the page scrolling behind it and let
  // Escape close it.
  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);

  const handleRequestDemoClick = useCallback((): void => {
    trackMarketingEvent('hero_cta_click', {
      location: 'home_hero',
      cta: 'request_demo',
    });

    if (isHomePage) {
      scrollToDemoForm();
      setIsMenuOpen(false);
      return;
    }

    navigate({ pathname: '/', hash: '#demo-form' });
    setIsMenuOpen(false);
  }, [isHomePage, navigate]);

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-50 px-6 py-4 backdrop-blur-xl md:px-12',
          isHomePage
            ? 'border-b border-[var(--ir-line)]/80 bg-[rgba(251,250,247,0.92)]'
            : 'border-b border-brand-primary/10 bg-white/92'
        )}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <BrandLogo wordmarkVariant={isHomePage ? 'editorial' : 'app'} />

          <nav
            aria-label={t('nav.primary')}
            className="hidden items-center gap-5 xl:flex"
          >
            {ROUTE_NAV_ITEMS.map((item) =>
              item.external ? (
                <a
                  key={item.to}
                  href={item.to}
                  className="inline-flex min-h-11 items-center whitespace-nowrap text-xs font-bold uppercase tracking-[0.08em] text-[var(--ir-muted)] transition-colors hover:text-[var(--ir-ink)]"
                >
                  {t(item.labelKey)}
                </a>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      'inline-flex min-h-11 items-center whitespace-nowrap text-xs font-bold uppercase tracking-[0.08em] transition-colors',
                      isActive
                        ? 'text-[var(--ir-ink)]'
                        : 'text-[var(--ir-muted)] hover:text-[var(--ir-ink)]'
                    )
                  }
                >
                  {t(item.labelKey)}
                </NavLink>
              )
            )}
            <Link
              to="/login"
              className="inline-flex min-h-11 items-center whitespace-nowrap text-xs font-bold uppercase tracking-[0.08em] text-[var(--ir-muted)] transition-colors hover:text-[var(--ir-ink)]"
            >
              {t('nav.login')}
            </Link>
            {isHomePage ? (
              <HomeButton
                type="button"
                variant="accent"
                size="sm"
                className="min-h-11 whitespace-nowrap text-xs font-bold uppercase tracking-[0.08em]"
                onClick={handleRequestDemoClick}
              >
                {t('cta.requestDemo')}
              </HomeButton>
            ) : (
              <MarketingButton
                type="button"
                variant="accent"
                size="sm"
                className="blueprint-border min-h-11 whitespace-nowrap text-xs font-bold uppercase tracking-[0.08em]"
                onClick={handleRequestDemoClick}
              >
                {t('cta.requestDemo')}
              </MarketingButton>
            )}
            <LanguageSwitcher />
          </nav>

          <div className="flex items-center gap-2 xl:hidden">
            {/* Tablet widths hide the full nav; keep the main action visible. */}
            <button
              type="button"
              onClick={handleRequestDemoClick}
              className={cn(
                isHomePage
                  ? homeButtonClasses('accent', 'sm')
                  : editorialButtonClasses('accent', 'sm'),
                'min-h-11 whitespace-nowrap text-xs font-bold uppercase tracking-[0.08em] max-md:hidden'
              )}
            >
              {t('cta.requestDemo')}
            </button>
            <button
              type="button"
              className="-mr-2.5 flex h-11 w-11 items-center justify-center rounded-full text-[var(--ir-ink)]"
              onClick={() => setIsMenuOpen((previous) => !previous)}
              aria-label={isMenuOpen ? t('nav.closeMenu') : t('nav.toggleMenu')}
              aria-expanded={isMenuOpen}
              aria-controls={MOBILE_MENU_ID}
            >
              {isMenuOpen ? (
                <X aria-hidden="true" />
              ) : (
                <Menu aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </header>

      {isMenuOpen ? (
        <nav
          id={MOBILE_MENU_ID}
          aria-label={t('nav.primary')}
          className="fixed inset-0 z-40 overflow-y-auto bg-[var(--ir-ivory)] px-6 pb-10 pt-24 md:px-12 xl:hidden"
        >
          <div className="flex flex-col gap-3">
            {ROUTE_NAV_ITEMS.map((item) =>
              item.external ? (
                <a
                  key={item.to}
                  href={item.to}
                  className="flex min-h-11 items-center text-2xl font-bold text-[var(--ir-ink)]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t(item.labelKey)}
                </a>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className="flex min-h-11 items-center text-2xl font-bold text-[var(--ir-ink)]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t(item.labelKey)}
                </NavLink>
              )
            )}
            <hr className="my-2 border-[var(--ir-line)]" />
            <Link
              to="/login"
              className="flex min-h-11 items-center text-2xl font-bold text-[var(--ir-ink)]"
              onClick={() => setIsMenuOpen(false)}
            >
              {t('nav.login')}
            </Link>
            <button
              type="button"
              onClick={handleRequestDemoClick}
              className={cn(
                isHomePage
                  ? homeButtonClasses('accent', 'lg')
                  : editorialButtonClasses('accent', 'lg'),
                !isHomePage && 'blueprint-border w-full',
                isHomePage && 'w-full'
              )}
            >
              {t('cta.requestDemo')}
            </button>
            <LanguageSwitcher className="self-start" />
          </div>
        </nav>
      ) : null}
    </>
  );
}
